import React, { useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import "./style.scss";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { CSV_TYPE } from "utils/constants";
import { formatDateTime } from "utils/time-utils";
import { exportExcel, findObjectVal } from "utils/utils";
import { LocalDate } from "@js-joda/core";
import { downloadBlob } from "../product-template-table/utils/utils";

interface IProps {
  columns: Array<{ identifier: string; title: string }>;
  content: Array<{ value: string | number; date: Date }>;
}

interface IDataForCsv {
  Date: string | Date;
  [key: string]: string | number | Date;
}
const RawDatasetsTable = ({ data }: { data: IProps }) => {
  const dt = useRef<DataTable>(null);
  const maxDatasetPerPage: number = 15;

  const extractJSONFromProps = () => {
    return data.content.map(row => {
      const newRow: IDataForCsv = { Date: row.date };
      newRow.Date = formatDateTime(row.date, "yyyy-MM-dd, HH:mm");
      data.columns.forEach(col => {
        newRow[col.title] = findObjectVal(row, col.identifier)?.toString() || "-";
      });
      return newRow;
    });
  };

  const arrayToCsv = (dataForCsv: IDataForCsv[]) => {
    const csvRows = [];
    const headers = Object.keys(dataForCsv[0]);
    csvRows.push(headers.join(","));
    dataForCsv.forEach(row => {
      const values = headers.map(header => {
        const val = row[header];
        return typeof val === "string" ? `"${val.replace(/"/g, '""')}"` : val;
      });
      csvRows.push(values.join(","));
    });
    return csvRows.join("\n");
  };

  const exportCSV = () => {
    const jsonData = extractJSONFromProps();
    const csvData = arrayToCsv(jsonData);
    downloadBlob(
      new Blob([csvData], { type: CSV_TYPE }),
      `data-${LocalDate.now().toString()}.csv`,
      CSV_TYPE
    );
  };

  const handleHeaderTemplate = (identifier: string) => {
    return <DatasetTitle identifier={identifier} hideData />;
  };

  const handleBodyTemplate = (
    rowData: { timestamp: string; date: Date; [identifier: string]: string | Date | null },
    identifier: string
  ) => {
    const cellData = rowData[identifier];
    return <div>{cellData?.toString() ?? "-"}</div>;
  };

  const handleExportExcel = () => {
    const tableData = data.content.map(row => {
      const newData: any = {};
      newData.Date = formatDateTime(row.date, "yyyy-MM-dd, HH:mm");
      data.columns.forEach(col => {
        newData[col.title] = findObjectVal(row, col.identifier)?.toString();
      });
      return newData;
    });
    exportExcel(tableData, "excel");
  }

  return (
    <div className="flex flex-column gap-2">
      <div className="text-right">
        <Button
          type="button"
          icon="pi pi-file"
          onClick={exportCSV}
          className="ml-auto p-button-sm mr-2"
          data-pr-tooltip="CSV"
          label="CSV"
        />
        <Button
          type="button"
          icon="pi pi-file-excel"
          onClick={handleExportExcel}
          className="ml-auto p-button-sm p-button-success"
          data-pr-tooltip="XLS"
          label="XLS"
        />
      </div>
      <div className="w-full h-full">
        <DataTable
          value={data.content}
          paginator={data.content?.length > maxDatasetPerPage}
          responsiveLayout="scroll"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          rows={maxDatasetPerPage}
          size="small"
          scrollable
          ref={dt}
        >
          <Column
            header="Date"
            field="date"
            body={rowData => formatDateTime(rowData.date, "yyyy-MM-dd, HH:mm")}
          />
          {data.columns.map(column => (
            <Column
              key={column.identifier}
              field={column.identifier}
              header={() => handleHeaderTemplate(column.identifier)}
              body={rowData => handleBodyTemplate(rowData, column.identifier)}
            />
          ))}
        </DataTable>
      </div>
    </div>
  );
};

export default RawDatasetsTable;
